import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CharacterPortrait from './CharacterPortrait.jsx';
import Icon from './Icon.jsx';
import useFillViewport from '../hooks/useFillViewport.js';
import { CHARACTERS_BY_ID } from '../data/characters.js';
import { boardName, FACE_QUESTIONS } from '../data/faces.js';
import { copyToClipboard } from '../lib/daily.js';
import { playCorrect, playWrong } from '../lib/sfx.js';
import { dealBoard, rankFaces, scoreFaces, shareFaces } from '../lib/twentyFaces.js';

/**
 * Twenty Faces.
 *
 * ── What it is ───────────────────────────────────────────────────────────────
 *
 * Twenty portraits, face up, and one of them is the person to find. Ask a
 * yes-or-no question, and every face that disagrees with the answer turns over.
 * Name the one left standing.
 *
 * ── Why it earns its place ───────────────────────────────────────────────────
 *
 * The app already had thirty character profiles that almost nobody read, and a
 * portrait system that draws all thirty from one illustration so they sit
 * together in a grid without drifting. This mode is what those two things were
 * always for: it turns a reference page into something with a win condition,
 * and it ends by handing the player the profile of whoever they just spent five
 * questions thinking about.
 *
 * Half the questions are about the picture, which is what Guess Who has always
 * been. The other half are about the person, and each of those carries its
 * references — so the game teaches who fished, who prophesied, who served a
 * foreign king, and which three deaths scripture actually records.
 *
 * ── The part that is checked rather than trusted ─────────────────────────────
 *
 * The board is only winnable if no two people answer every question the same
 * way. `scripts/check-faces.mjs` proves that over the full cast and plays four
 * thousand deals: solvable every time, mean 4.4 questions, worst case 6.
 *
 * ── Layout ───────────────────────────────────────────────────────────────────
 *
 * The whole screen is measured rather than expressed in vh — see
 * useFillViewport for why. A player who has to scroll to reach the question
 * button has already lost sight of the board they are asking about.
 */

export default function TwentyFaces({
  best = 0,
  runs = 0,
  signedIn = false,
  soundOn = true,
  onNeedAccount,
  onUpgrade,
  onFinish,
  onExit,
}) {
  const [deal, setDeal] = useState(() => dealBoard());
  const [phase, setPhase] = useState('intro'); // intro | play | won
  const [down, setDown] = useState(() => new Set());
  const [asked, setAsked] = useState([]); // [{ id, answer }]
  const [wrong, setWrong] = useState(0);
  const [verdict, setVerdict] = useState(null); // { id, answer } | { miss: name }
  const [sheet, setSheet] = useState(null); // null | { kind: 'ask' } | { kind: 'name', id }
  const [copied, setCopied] = useState(false);

  const sectionRef = useRef(null);
  const timersRef = useRef([]);
  const later = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);
  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    },
    [],
  );

  const secret = CHARACTERS_BY_ID[deal.secretId];
  const standing = deal.faces.filter((c) => !down.has(c.id));
  const askedIds = useMemo(() => new Set(asked.map((a) => a.id)), [asked]);

  /*
   * One height for the whole game, measured once per phase.
   *
   * Everything inside is flex, so the board takes what is left after the bar
   * and the foot rather than being given a fraction of the viewport that
   * happens to be wrong on half of the phones people actually have.
   */
  useFillViewport(
    sectionRef,
    phase,
    (room) => {
      const section = sectionRef.current;
      if (section) section.style.height = `${Math.round(Math.max(320, room))}px`;
    },
    [phase],
  );

  /* ── playing ─────────────────────────────────────────────────────────────── */

  const begin = () => {
    setDeal(dealBoard());
    setDown(new Set());
    setAsked([]);
    setWrong(0);
    setVerdict(null);
    setSheet(null);
    setPhase('play');
  };

  const ask = (question) => {
    if (phase !== 'play' || askedIds.has(question.id)) return;
    const answer = question.test(secret);
    setAsked((list) => [...list, { id: question.id, answer }]);
    setVerdict({ id: question.id, answer });
    setSheet(null);
    /*
     * Everyone who disagrees turns over — including faces the player had
     * already turned over by hand, which is why this unions rather than
     * replaces. Turning a face back up is allowed and is a way of taking a
     * manual elimination back; a question's eliminations are facts and stay.
     */
    setDown((prev) => {
      const next = new Set(prev);
      for (const c of deal.faces) if (question.test(c) !== answer) next.add(c.id);
      return next;
    });
  };

  const toggle = (c) => {
    if (phase !== 'play') return;
    if (down.has(c.id)) {
      setDown((prev) => {
        const next = new Set(prev);
        next.delete(c.id);
        return next;
      });
      return;
    }
    setSheet({ kind: 'name', id: c.id });
  };

  const ruleOut = (c) => {
    setDown((prev) => new Set(prev).add(c.id));
    setSheet(null);
  };

  const name = (c) => {
    setSheet(null);
    if (c.id === deal.secretId) {
      if (soundOn) playCorrect();
      setPhase('won');
      onFinish?.(scoreFaces(asked.length, wrong), {
        questions: asked.length,
        wrong,
        id: c.id,
      });
      return;
    }
    if (soundOn) playWrong();
    setWrong((n) => n + 1);
    setDown((prev) => new Set(prev).add(c.id));
    setVerdict({ miss: c.name });
  };

  /* ── the ending ──────────────────────────────────────────────────────────── */

  const score = scoreFaces(asked.length, wrong);
  const shareable = useMemo(
    () => shareFaces(secret.name, asked.length, wrong, score),
    [secret.name, asked.length, wrong, score],
  );

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareable });
        return;
      } catch {
        /* dismissed — fall through to copy */
      }
    }
    if (await copyToClipboard(shareable)) {
      setCopied(true);
      later(() => setCopied(false), 2000);
    }
  };

  // One complete board, then an account. `runs` already counts the board that
  // has just ended, so this is true on the first ending an anonymous player
  // sees — after they have played, never before.
  const gate = !signedIn && runs >= 1;

  /* ── the answer strip ────────────────────────────────────────────────────── */

  let strip;
  if (verdict?.miss) {
    strip = (
      <p className="tf__verdict is-miss">
        <Icon name="close" size={14} />
        <span>Not {verdict.miss}. Keep looking.</span>
      </p>
    );
  } else if (verdict) {
    const q = FACE_QUESTIONS.find((item) => item.id === verdict.id);
    strip = (
      <div className="tf__verdict">
        <p className="tf__asked">{q.ask}</p>
        <p className={`tf__answer${verdict.answer ? ' is-yes' : ''}`}>
          <Icon name={verdict.answer ? 'check' : 'close'} size={14} />
          <span>{verdict.answer ? q.yes : q.no}</span>
        </p>
        {q.note ? <p className="tf__note">{q.note}</p> : null}
        {q.ref ? <p className="tf__ref">{q.ref}</p> : null}
      </div>
    );
  } else {
    strip = (
      <p className="tf__verdict is-quiet">
        One of these twenty. Ask something, or tap a face to name them.
      </p>
    );
  }

  /* ── screens ─────────────────────────────────────────────────────────────── */

  if (phase === 'intro') {
    return (
      <section ref={sectionRef} className="screen tf tf--rest">
        <div className="tf__panel tf__panel--intro">
          <p className="tf__eyebrow">Thirty faces · twenty on the board</p>
          <h2 className="tf__title">Twenty Faces</h2>
          <p className="tf__lede">
            One of the twenty is the person to find. Ask a yes-or-no question and every face that
            disagrees turns over. Name the one left standing.
          </p>
          <ul className="tf__how">
            <li>
              <b>Ask about the card.</b> Beard, head covering, the colour of the robe, and the gold
              emblem each figure is traditionally shown with.
            </li>
            <li>
              <b>Or ask about the life.</b> Who fished for a living, who scripture calls a prophet,
              who served inside a foreign court. Every answer gives you the reference.
            </li>
            <li>
              <b>Fewer questions is a better score.</b> A wrong name costs more than three
              questions, so it pays to narrow it down.
            </li>
          </ul>
          <p className="tf__caveat">
            The portraits are drawn, not sourced — nobody knows what a first-century fisherman
            looked like. The clothes and emblems are how these figures have been shown for
            centuries; the questions about their lives are from scripture.
          </p>
          <button type="button" className="btn btn--primary btn--block btn--lg" onClick={begin}>
            Deal the board
          </button>
          <button type="button" className="btn btn--ghost btn--block" onClick={onExit}>
            Back to practice
          </button>
        </div>
      </section>
    );
  }

  if (phase === 'won') {
    return (
      <section ref={sectionRef} className="screen tf tf--rest">
        <div className="tf__panel">
          <p className="tf__eyebrow">
            Named in {asked.length} question{asked.length === 1 ? '' : 's'}
            {wrong ? ` · ${wrong} wrong ${wrong === 1 ? 'name' : 'names'}` : ''}
          </p>
          <div className="tf__reveal">
            <CharacterPortrait portrait={secret.portrait} size={116} />
            <div>
              <h2 className="tf__title">{secret.name}</h2>
              <p className="tf__epithet">{secret.epithet}</p>
              <p className="tf__group">
                {secret.group} · {secret.era}
              </p>
            </div>
          </div>
          <p className="tf__scripture">{secret.verse.text}</p>
          <p className="tf__ref">{secret.verse.ref}</p>
          {/* One scene, not the whole profile. The point of the reveal is that
              the face the player has spent five questions staring at turns out
              to have done something — the full profile is a tab away. */}
          {secret.moments?.[0] ? (
            <>
              <p className="tf__moment-title">{secret.moments[0].title}</p>
              <p className="tf__moment">{secret.moments[0].text}</p>
              <p className="tf__ref">{secret.moments[0].ref}</p>
            </>
          ) : null}
          <div className="tf__tally">
            <div>
              <b>{asked.length}</b>
              <span>question{asked.length === 1 ? '' : 's'}</span>
            </div>
            <div>
              <b>{score}</b>
              <span>score</span>
            </div>
            <div>
              <b>{Math.max(best, score)}</b>
              <span>best</span>
            </div>
          </div>
          <p className="tf__rank">{rankFaces(score)}</p>
          {gate ? (
            <div className="tf__gate">
              <p className="tf__gate-lead">
                {score > best ? 'That is your best board.' : `${score}.`} It lives in this browser
                only — clear it and it is gone.
              </p>
              <button
                type="button"
                className="btn btn--primary btn--block btn--lg"
                onClick={onNeedAccount}
              >
                Make a free account to keep playing
              </button>
              <p className="tf__gate-note">
                Free, takes a moment. Keeps your best board and carries it to your phone.
              </p>
              <button type="button" className="tf__gate-more" onClick={onUpgrade}>
                Or see what Premium opens up
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn--primary btn--block btn--lg" onClick={begin}>
              Deal another twenty
            </button>
          )}
          <button type="button" className="btn btn--ghost btn--block" onClick={share}>
            <Icon name="share" size={15} />
            {copied ? 'Copied' : 'Share it'}
          </button>
          <button type="button" className="btn btn--ghost btn--block" onClick={onExit}>
            Back to practice
          </button>
        </div>
      </section>
    );
  }

  const naming = sheet?.kind === 'name' ? CHARACTERS_BY_ID[sheet.id] : null;

  return (
    <section ref={sectionRef} className="screen tf">
      <div className="tf__bar">
        <button type="button" className="icon-btn" onClick={onExit} aria-label="Leave the board">
          <Icon name="close" size={16} />
        </button>
        <p className="tf__count">
          <b>{standing.length}</b> still standing
        </p>
        <p className="tf__count tf__count--right">
          <b>{asked.length}</b> asked
        </p>
      </div>

      <div className="tf__board">
        {deal.faces.map((c, i) => {
          const isDown = down.has(c.id);
          return (
            <button
              key={c.id}
              type="button"
              className={`tf__card${isDown ? ' is-down' : ''}`}
              // A diagonal wave rather than all twenty at once: the eye can
              // follow which faces went, which is the information the flip is
              // there to carry.
              style={{ transitionDelay: `${((i % 4) + Math.floor(i / 4)) * 26}ms` }}
              onClick={() => toggle(c)}
              aria-label={isDown ? `${c.name}, ruled out — turn back up` : `Name ${c.name}`}
              aria-pressed={isDown}
            >
              <span className="tf__flip">
                <span className="tf__front">
                  {/* The name gets its own strip rather than sitting over the
                      picture. Laid over it, a two-line name — "James
                      (Alphaeus)", "Simon the Zealot" — covered the emblem
                      badge in the bottom corner, and the emblem is what four
                      of the questions are about. */}
                  <span className="tf__pic">
                    <CharacterPortrait portrait={c.portrait} size={120} />
                  </span>
                  <span className="tf__name">{boardName(c)}</span>
                </span>
                <span className="tf__back" aria-hidden="true" />
              </span>
            </button>
          );
        })}
      </div>

      <div className="tf__foot">
        {strip}
        <button
          type="button"
          className="btn btn--primary btn--block btn--lg"
          onClick={() => setSheet({ kind: 'ask' })}
        >
          Ask a question
        </button>
      </div>

      {sheet ? (
        <div className="tf__sheet-wrap">
          <button
            type="button"
            className="tf__scrim"
            onClick={() => setSheet(null)}
            aria-label="Close"
          />
          {naming ? (
            <div className="tf__sheet tf__sheet--name" role="dialog" aria-label="Name this face">
              <div className="tf__nominee">
                <CharacterPortrait portrait={naming.portrait} size={72} />
                <div>
                  <h3>{naming.name}</h3>
                  <p>{naming.epithet}</p>
                </div>
              </div>
              <button
                type="button"
                className="btn btn--primary btn--block btn--lg"
                onClick={() => name(naming)}
              >
                This is them
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--block"
                onClick={() => ruleOut(naming)}
              >
                Not them — turn the card over
              </button>
              <button type="button" className="btn btn--ghost btn--block" onClick={() => setSheet(null)}>
                Back to the board
              </button>
            </div>
          ) : (
            <div className="tf__sheet" role="dialog" aria-label="Ask a question">
              <p className="tf__sheet-head">The card in front of you</p>
              <div className="tf__qs">
                {FACE_QUESTIONS.filter((q) => q.group === 'look').map((q) => (
                  <QuestionChip key={q.id} q={q} asked={asked} onAsk={ask} />
                ))}
              </div>
              <p className="tf__sheet-head">The person behind it</p>
              <div className="tf__qs">
                {FACE_QUESTIONS.filter((q) => q.group === 'life').map((q) => (
                  <QuestionChip key={q.id} q={q} asked={asked} onAsk={ask} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

/**
 * One question in the sheet.
 *
 * Questions that can no longer split the board are deliberately *not* greyed
 * out. Doing that would tell the player the answer for free — if the game
 * refuses to ask "is this a woman?", it has just said the remaining faces are
 * all the same sex. A question already asked shows the answer it gave, which
 * is information the player already has.
 */
function QuestionChip({ q, asked, onAsk }) {
  const prior = asked.find((a) => a.id === q.id);
  return (
    <button
      type="button"
      className={`tf__q${prior ? ` is-asked${prior.answer ? ' is-yes' : ''}` : ''}`}
      disabled={Boolean(prior)}
      onClick={() => onAsk(q)}
    >
      <span>{q.ask}</span>
      {prior ? <b>{prior.answer ? 'Yes' : 'No'}</b> : null}
    </button>
  );
}
